import { SupportedCurrency } from "@/lib/currencyDetection";
import { CREDIT_COSTS } from "./creditCosts";

export interface PriceInfo {
  amount: number;
  stripeId: string | null;
}

// AI Meal Plan credit costs (per meal type per day × duration) - uses centralized config
export const AI_MEAL_PLAN_CREDITS = {
  PAID_RATE: CREDIT_COSTS.aiMealPlanPaidPerDay,    // Credits per meal type per day for Pro/Chef Master
  FREE_RATE: CREDIT_COSTS.aiMealPlanFreePerDay,    // Credits per meal type per day for Free tier
  DAYS_PER_WEEK: 7,
  // Calculated costs for 1-week plans:
  // 1 meal type: Paid = 70 credits, Free = 105 credits
  // 2 meal types: Paid = 140 credits, Free = 210 credits
  // 3 meal types: Paid = 210 credits, Free = 315 credits
  // 4 meal types: Paid = 280 credits, Free = 420 credits
  // 
  // Calculated costs for 4-week plans (Pro/Chef Master only):
  // 1 meal type: 280 credits
  // 2 meal types: 560 credits
  // 3 meal types: 840 credits
  // 4 meal types: 1120 credits
} as const;

export interface CreditPackPriceInfo {
  amount: number; // in cents/øre
  credits: number;
  savingsPercent: number;
}

// Subscription prices (amounts are in normal currency units, Stripe priceId holds the real price)
export const SUBSCRIPTION_PRICES: Record<"free" | "pro" | "chef_master", Record<SupportedCurrency, PriceInfo>> = {
  free: {
    DKK: { amount: 0, stripeId: null },
    EUR: { amount: 0, stripeId: null },
    USD: { amount: 0, stripeId: null },
  },
  pro: {
    DKK: { amount: 75, stripeId: "price_1T1yK4CT2SL2i7RxUOvSDVte" },
    EUR: { amount: 9.99, stripeId: "price_1T1yK5CT2SL2i7RxXDpOGSFj" },
    USD: { amount: 12.99, stripeId: "price_1T1yK6CT2SL2i7Rx9Zbu4etr" },
  },
  chef_master: {
    DKK: { amount: 195, stripeId: "price_1SyuarCT2SL2i7RxMgIahDnJ" },
    EUR: { amount: 26, stripeId: "price_1SyubACT2SL2i7RxMQaQacNm" },
    USD: { amount: 28.99, stripeId: "price_1SyubOCT2SL2i7RxQNgacUV4" },
  },
};

// Yearly subscription prices (~15% discount)
export const SUBSCRIPTION_PRICES_YEARLY: Record<"pro" | "chef_master", Record<SupportedCurrency, PriceInfo>> = {
  pro: {
    DKK: { amount: 765, stripeId: "price_1T3OACCT2SL2i7RxPjXntiPi" },
    EUR: { amount: 103, stripeId: "price_1T3OADCT2SL2i7RxAou7Xgh2" },
    USD: { amount: 121, stripeId: "price_1T3OAECT2SL2i7RxjT7Qoq8n" },
  },
  chef_master: {
    DKK: { amount: 1989, stripeId: "price_1T3OAFCT2SL2i7RxyHuiEnUh" },
    EUR: { amount: 267, stripeId: "price_1T3OAFCT2SL2i7Rxlr86OV4B" },
    USD: { amount: 314, stripeId: "price_1T3OAGCT2SL2i7Rx1u2iXT69" },
  },
};

// Credit pack price table (amounts in cents/øre)
export const CREDIT_PACKAGE_PRICES: Record<string, Record<SupportedCurrency, CreditPackPriceInfo>> = {
  starter: {
    DKK: { amount: 4900, credits: 50, savingsPercent: 10 },
    EUR: { amount: 499, credits: 50, savingsPercent: 10 },
    USD: { amount: 599, credits: 50, savingsPercent: 10 },
  },
  popular: {
    DKK: { amount: 12900, credits: 150, savingsPercent: 15 },
    EUR: { amount: 1299, credits: 150, savingsPercent: 15 },
    USD: { amount: 1599, credits: 150, savingsPercent: 15 },
  },
  power: {
    DKK: { amount: 22900, credits: 300, savingsPercent: 20 },
    EUR: { amount: 2299, credits: 300, savingsPercent: 20 },
    USD: { amount: 2899, credits: 300, savingsPercent: 20 },
  },
  mega: {
    DKK: { amount: 34900, credits: 500, savingsPercent: 25 },
    EUR: { amount: 3499, credits: 500, savingsPercent: 25 },
    USD: { amount: 4499, credits: 500, savingsPercent: 25 },
  },
  ultra: {
    DKK: { amount: 49900, credits: 5000, savingsPercent: 50 },
    EUR: { amount: 6675, credits: 5000, savingsPercent: 50 },
    USD: { amount: 7900, credits: 5000, savingsPercent: 50 },
  },
};

// Multi-currency Stripe IDs for credit packs
export interface CreditPackStripeIdsByCurrency {
  DKK: string;
  EUR: string;
  USD: string;
}

export const CREDIT_PACKAGE_STRIPE_IDS: Record<"starter" | "popular" | "power" | "mega" | "ultra", CreditPackStripeIdsByCurrency> = {
  starter: {
    DKK: "price_1SUm9UCT2SL2i7RxtNuuY3qh",
    EUR: "price_1SbLxzCT2SL2i7RxvRGgoHNJ",
    USD: "price_1SbLyXCT2SL2i7Rx4bphnQ0g",
  },
  popular: {
    DKK: "price_1SUmA4CT2SL2i7Rxc1G9kk1J",
    EUR: "price_1SbLyiCT2SL2i7RxFjkzmWop",
    USD: "price_1SbLytCT2SL2i7RxEUlCGaZ1",
  },
  power: {
    DKK: "price_1SUmAICT2SL2i7RxPVEshtDU",
    EUR: "price_1SbLz3CT2SL2i7RxC8eQKipI",
    USD: "price_1SbLzCCT2SL2i7RxY1CarjAF",
  },
  mega: {
    DKK: "price_1SUmAgCT2SL2i7RxqDt9A0Qu",
    EUR: "price_1SbLzOCT2SL2i7Rx3jFL05Fo",
    USD: "price_1SbLzZCT2SL2i7Rxcq5krDt9",
  },
  ultra: {
    DKK: "price_1U3PlzCT2SL2i7RxG6kmNJQd",
    EUR: "price_1U3Pm0CT2SL2i7RxSWDe8XxK",
    USD: "price_1U3Pm2CT2SL2i7Rx4ILhLV89",
  },
};

// Token pack prices (amounts in cents/øre)
export interface TokenPackPriceInfo {
  amount: number; // in cents/øre
  tokens: number;
  bonus: number;
}

export const TOKEN_PACK_PRICES: Record<string, Record<SupportedCurrency, TokenPackPriceInfo>> = {
  pack_small: {
    DKK: { amount: 2900, tokens: 29, bonus: 0 },
    EUR: { amount: 399, tokens: 29, bonus: 0 },
    USD: { amount: 399, tokens: 29, bonus: 0 },
  },
  pack_medium: {
    DKK: { amount: 9900, tokens: 110, bonus: 11 },
    EUR: { amount: 1299, tokens: 110, bonus: 11 },
    USD: { amount: 1499, tokens: 110, bonus: 11 },
  },
  pack_large: {
    DKK: { amount: 19900, tokens: 230, bonus: 31 },
    EUR: { amount: 2699, tokens: 230, bonus: 31 },
    USD: { amount: 2899, tokens: 230, bonus: 31 },
  },
  pack_mega: {
    // 774 + Chef Master 15% = 890 final tokens (beats Credit Ultra convert at the same price)
    DKK: { amount: 49900, tokens: 774, bonus: 0 },
    EUR: { amount: 6699, tokens: 774, bonus: 0 },
    USD: { amount: 7299, tokens: 774, bonus: 0 },
  },
  pack_ultimate: {
    DKK: { amount: 98500, tokens: 2500, bonus: 200 },
    EUR: { amount: 13300, tokens: 2500, bonus: 200 },
    USD: { amount: 15700, tokens: 2500, bonus: 200 },
  },
};

// Recipe Book — display fallback; live checkout uses margin-based all-in pricing
// (see recipeBookPricing.ts — ≥20% margin inkl. standard levering)
export const RECIPE_BOOK_PRICES: Record<SupportedCurrency, { amount: number; display: string }> = {
  DKK: { amount: 36900, display: "fra 369 kr" },
  EUR: { amount: 4999, display: "from €49.99" },
  USD: { amount: 5499, display: "from $54.99" },
};

/** Optional Stripe catalog IDs — checkout uses price_data (dynamic by page count) */
export const RECIPE_BOOK_STRIPE_IDS: Record<SupportedCurrency, string | null> = {
  DKK: null,
  EUR: null,
  USD: null,
};

// GLS Delivery prices (amounts in cents/øre)
export interface DeliveryPriceInfo {
  amount: number; // in cents/øre
  display: string;
}

export const DELIVERY_PRICES: Record<"standard" | "express", Record<SupportedCurrency, DeliveryPriceInfo>> = {
  standard: {
    DKK: { amount: 7900, display: "79 kr" },
    EUR: { amount: 1099, display: "€10.99" },
    USD: { amount: 1199, display: "$11.99" },
  },
  express: {
    DKK: { amount: 12900, display: "129 kr" },
    EUR: { amount: 1799, display: "€17.99" },
    USD: { amount: 1999, display: "$19.99" },
  },
};

// ── Creator Merch Store Delivery (15% platform fee markup) ──

export const CREATOR_DELIVERY_MARKUP = 0.15;

export const CREATOR_DELIVERY_PRICES: Record<"standard" | "express", Record<SupportedCurrency, DeliveryPriceInfo>> = {
  standard: {
    DKK: { amount: 9085, display: "91 kr" },
    EUR: { amount: 1264, display: "€12.64" },
    USD: { amount: 1379, display: "$13.79" },
  },
  express: {
    DKK: { amount: 14835, display: "149 kr" },
    EUR: { amount: 2069, display: "€20.69" },
    USD: { amount: 2299, display: "$22.99" },
  },
};

// Extended Insurance add-on prices (amounts in cents/øre) — not recommended
export const INSURANCE_PRICES: Record<SupportedCurrency, DeliveryPriceInfo> = {
  DKK: { amount: 9900, display: "99 kr" },
  EUR: { amount: 1399, display: "€13.99" },
  USD: { amount: 1600, display: "$16" },
};

// Return fee prices (amounts in cents/øre)
export const RETURN_FEE_PRICES: Record<SupportedCurrency, DeliveryPriceInfo> = {
  DKK: { amount: 3900, display: "39 kr" },
  EUR: { amount: 550, display: "€5.50" },
  USD: { amount: 699, display: "$6.99" },
};

// Placeholder Stripe price IDs for insurance — replace with real IDs from Stripe dashboard
export const INSURANCE_STRIPE_IDS: CreditPackStripeIdsByCurrency = {
  DKK: "price_INSURANCE_DKK_PLACEHOLDER",
  EUR: "price_INSURANCE_EUR_PLACEHOLDER",
  USD: "price_INSURANCE_USD_PLACEHOLDER",
};

// Placeholder Stripe price IDs for delivery fees — replace with real IDs from Stripe dashboard
export const DELIVERY_STRIPE_IDS: Record<"standard" | "express", CreditPackStripeIdsByCurrency> = {
  standard: {
    DKK: "price_DELIVERY_STANDARD_DKK_PLACEHOLDER",
    EUR: "price_DELIVERY_STANDARD_EUR_PLACEHOLDER",
    USD: "price_DELIVERY_STANDARD_USD_PLACEHOLDER",
  },
  express: {
    DKK: "price_DELIVERY_EXPRESS_DKK_PLACEHOLDER",
    EUR: "price_DELIVERY_EXPRESS_EUR_PLACEHOLDER",
    USD: "price_DELIVERY_EXPRESS_USD_PLACEHOLDER",
  },
};

export const TOKEN_PACK_STRIPE_IDS: Record<string, CreditPackStripeIdsByCurrency> = {
  pack_small: {
    DKK: "price_1T1ddPCT2SL2i7Rx6kB8Tuoy",
    EUR: "price_1T1ddQCT2SL2i7Rx26Ck9cKH",
    USD: "price_1T1ddRCT2SL2i7Rx4iw7a403",
  },
  pack_medium: {
    DKK: "price_1T1ddSCT2SL2i7RxdGfRaIXt",
    EUR: "price_1T1ddTCT2SL2i7RxRdb2CSPK",
    USD: "price_1T1ddUCT2SL2i7RxmJNN0xnA",
  },
  pack_large: {
    DKK: "price_1T1ddVCT2SL2i7RxXAp9a93X",
    EUR: "price_1T1ddWCT2SL2i7Rx8yVY5yJY",
    USD: "price_1T1ddXCT2SL2i7Rxaq9WO8MB",
  },
  pack_mega: {
    DKK: "price_1T1ddYCT2SL2i7Rx4bwLMTVo",
    EUR: "price_1T1ddZCT2SL2i7Rxd6N0Id5S",
    USD: "price_1T1ddaCT2SL2i7Rxp88F8C9n",
  },
  pack_ultimate: {
    DKK: "price_1U5lfgCT2SL2i7RxJhvxyjxN",
    EUR: "price_1U5lfhCT2SL2i7RxVhywZNag",
    USD: "price_1U5lfjCT2SL2i7RxrbWxlKOe",
  },
};

// ── Extra Family Slot Prices (per slot/month, amounts in cents/øre) ──

export interface ExtraFamilySlotPriceInfo {
  amount: number; // in cents/øre
  stripeId: string;
}

export const EXTRA_FAMILY_SLOT_PRICES: Record<SupportedCurrency, ExtraFamilySlotPriceInfo> = {
  DKK: { amount: 4900, stripeId: "price_1T6EEFCT2SL2i7Rxt5t5uTUP" },
  EUR: { amount: 650, stripeId: "price_1T6EEJCT2SL2i7RxtnUfwEOz" },
  USD: { amount: 699, stripeId: "price_1T6EEKCT2SL2i7RxA2Xl8CIG" },
};

export const EXTRA_FAMILY_SLOT_MAX = 5;

// Gift subscriptions are a one-time purchase (not a renewal). Price is at least 20%
// above the matching normal plan. Chef Master gifts are yearly only; Pro Chef can
// be monthly or yearly. Repeat gifts to the same person are allowed and stack time.
export const GIFT_SUBSCRIPTION_MARKUP = 1.2;
export type GiftSubscriptionTier = "pro" | "chef_master";
export type GiftInterval = "month" | "year";

export function isGiftIntervalAllowed(tier: GiftSubscriptionTier, interval: GiftInterval): boolean {
  if (tier === "chef_master") return interval === "year";
  return interval === "month" || interval === "year";
}

export function applyGiftMarkup(baseAmount: number, currency: SupportedCurrency): number {
  const marked = baseAmount * GIFT_SUBSCRIPTION_MARKUP;
  if (currency === "DKK") {
    return Math.ceil(marked - 1e-9);
  }
  return Math.ceil(marked * 100 - 1e-9) / 100;
}

export function getGiftSubscriptionPrice(
  tier: GiftSubscriptionTier,
  interval: GiftInterval,
  currency: SupportedCurrency,
): PriceInfo {
  if (!isGiftIntervalAllowed(tier, interval)) {
    throw new Error("Chef Master gifts are yearly only");
  }
  const table = interval === "year" ? SUBSCRIPTION_PRICES_YEARLY : SUBSCRIPTION_PRICES;
  const base = table[tier][currency].amount;
  return { amount: applyGiftMarkup(base, currency), stripeId: null };
}

export function getGiftSubscriptionUnitAmount(
  tier: GiftSubscriptionTier,
  interval: GiftInterval,
  currency: SupportedCurrency,
): number {
  return Math.round(getGiftSubscriptionPrice(tier, interval, currency).amount * 100);
}

/** @deprecated Use getGiftSubscriptionPrice(tier, interval, currency). Monthly Pro / yearly Chef Master display fallback. */
export const GIFT_SUBSCRIPTION_PRICES: Record<"pro" | "chef_master", Record<SupportedCurrency, PriceInfo>> = {
  pro: {
    DKK: getGiftSubscriptionPrice("pro", "month", "DKK"),
    EUR: getGiftSubscriptionPrice("pro", "month", "EUR"),
    USD: getGiftSubscriptionPrice("pro", "month", "USD"),
  },
  chef_master: {
    DKK: getGiftSubscriptionPrice("chef_master", "year", "DKK"),
    EUR: getGiftSubscriptionPrice("chef_master", "year", "EUR"),
    USD: getGiftSubscriptionPrice("chef_master", "year", "USD"),
  },
};

// ── MobilePay Prices (DKK-only, +5 DKK surcharge) ──

export const MOBILEPAY_SUBSCRIPTION_PRICES: Record<"pro" | "chef_master", PriceInfo> = {
  pro: { amount: 80, stripeId: "price_1T5r59CT2SL2i7RxYPdsFI4Q" },
  chef_master: { amount: 200, stripeId: "price_1T5r5ACT2SL2i7RxW0u73JIi" },
};

export const MOBILEPAY_CREDIT_PACK_PRICES: Record<string, { amount: number; credits: number; stripeId: string }> = {
  starter:  { amount: 5400,  credits: 50,  stripeId: "price_1T5r5ECT2SL2i7Rxz4AxJzgf" },
  popular:  { amount: 13400, credits: 150, stripeId: "price_1T5r5FCT2SL2i7RxirQQ4guY" },
  power:    { amount: 23400, credits: 300, stripeId: "price_1T5r5GCT2SL2i7Rx8QsnmyRJ" },
  mega:     { amount: 35400, credits: 500, stripeId: "price_1T5r5HCT2SL2i7RxUmBpwzEI" },
  ultra:    { amount: 50400, credits: 5000, stripeId: "price_1U3Pm3CT2SL2i7Rxu3ztXZH1" },
};

export const MOBILEPAY_TOKEN_PACK_PRICES: Record<string, { amount: number; tokens: number; stripeId: string }> = {
  pack_small:    { amount: 3400,  tokens: 29,   stripeId: "price_1T5r5ICT2SL2i7Rx8v36QmOH" },
  pack_medium:   { amount: 10400, tokens: 110,  stripeId: "price_1T5r5JCT2SL2i7RxZS8Y9Rv4" },
  pack_large:    { amount: 20400, tokens: 230,  stripeId: "price_1T5r5JCT2SL2i7Rxbja0nzzn" },
  pack_mega:     { amount: 50400, tokens: 774,  stripeId: "price_1T5r5LCT2SL2i7RxjPMQn14t" },
  pack_ultimate: { amount: 99000, tokens: 2500, stripeId: "price_1U5lfkCT2SL2i7RxbTaVBWC7" },
};
